import React from 'react';
import {makeStyles} from '@mui/styles';
import {Avatar, Box, Grid, Typography} from '@mui/material';
import {theme} from '../../../Theme';
import useScreenWidth from '../../../hooks/useScreenWidth';
import parser from 'html-react-parser';
import {Rating, Skeleton} from '@mui/material';

const useStyles = makeStyles(() => ({
  profileBox: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.gray.border}`,
  },
  reviewBox: {
    borderRadius: 6,
    boxShadow:
      window.innerWidth > 600
        ? '0px 7px 21px rgba(0, 0, 0, 0.17)'
        : '0px 5px 13px rgba(0, 0, 0, 0.17)',
    minHeight: window.innerWidth > 600 ? 200 : 0,
  },
}));

interface IProps {
  user: any;
  reviews: any[];
  loading: boolean;
}

export default function ProAboutSection({user, reviews, loading}: IProps) {
  const classes = useStyles();
  const width = useScreenWidth();

  const skeleton = (
    <Skeleton
      variant="rectangular"
      width={width > 600 ? '49%' : '100%'}
      height={180}
      style={{borderRadius: 10, marginBottom: 16}}
    />
  );

  return (
    <Box py={2} mx={width > 600 ? 0 : 2}>
      <Box p={2} mb={6} className={classes.profileBox}>
        <Grid container direction={width > 600 ? 'row' : 'column'}>
          <Avatar
            alt={user?.name?.split(' ')[0] || 'Avatar'}
            src={user.imgAttachmentRef?.url}
            style={{
              width: width > 600 ? 100 : 120,
              height: width > 600 ? 100 : 120,
              alignSelf: width > 600 ? 'flex-start' : 'center',
            }}
          />
          <Grid
            item
            style={{
              width: width > 600 ? '85%' : '100%',
              marginTop: width > 600 ? 0 : 16,
              marginLeft: width > 600 ? 16 : 0,
            }}>
            <Typography gutterBottom style={{fontSize: width > 600 ? 18 : 13}}>
              {parser(
                user.description ||
                  `Hi! My name is ${user.name} and I'm a professional ${
                    user.title || 'contractor'
                  } based in ${user.city}.`,
              )}
            </Typography>
            <Typography
              style={{
                fontSize: width > 600 ? 18 : 13,
                fontWeight: width > 600 ? 700 : 400,
              }}>
              - {user.name}, {user.companyName}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} style={{marginBottom: 16}}>
        {reviews
          .filter(
            (review) =>
              review.reviewType === 'BIDDER' &&
              review.rating > 0.88 &&
              review.note?.length > 0,
          )
          .sort(
            (a: any, b: any) =>
              b.rating - a.rating || b.note?.length - a.note?.length,
          )
          .slice(0, 4)
          .map((review) => (
            <Grid item xs={12} sm={6}>
              <Box p={width > 600 ? 3 : 2} className={classes.reviewBox}>
                <Grid container alignItems="center">
                  <Typography style={{fontSize: width > 600 ? 28 : 20}}>
                    {(review.rating * 5).toFixed(1)}/5
                  </Typography>
                  <Rating
                    name="reviewRating"
                    defaultValue={review.rating * 5}
                    precision={0.1}
                    readOnly
                    style={{
                      color: user.color || theme.palette.primary.main,
                      fontSize: width > 600 ? 34 : 25,
                      marginTop: -6,
                      marginLeft: 8,
                    }}
                  />
                </Grid>
                <Typography
                  gutterBottom
                  style={{
                    fontSize: width > 600 ? 18 : 13,
                    color: theme.palette.gray.dark,
                  }}>
                  {review.note}
                </Typography>
                <Typography
                  align="right"
                  style={{
                    fontSize: width > 600 ? 21 : 16,
                    textTransform: 'capitalize',
                  }}>
                  - {review.user.name}, {review.city}
                </Typography>
              </Box>
            </Grid>
          ))}
      </Grid>

      {loading && (
        <Grid container justifyContent="space-between">
          {skeleton}
          {skeleton}
          {skeleton}
          {skeleton}
        </Grid>
      )}
    </Box>
  );
}
